import deepmerge from 'deepmerge';

import { isBoolean, isInvalid } from '@/modules/validate';
import { ENV, formatterEnv, getEnv, Platform } from '@/modules/url/common';
import { isBrowser } from '@/modules/detector';
import { loggerError, loggerInfo } from '@/modules/logger';

import { LogModule } from '@/data/config';
import { ISetPluginOptions, setPluginUrl } from './pluginUrl';
import { getBrowser } from '../utils';

export { getPluginUrl } from './pluginUrl';
export * from './sensors';

//  -------------- 调试配置 -------------
export let isDebug = false;

/**
 * 废弃 - 设置调试日志输出
 * 采用 - setUtilsConfig方法
 */
export function setDebug(isSet: boolean) {
  isDebug = isSet;
}

type IUtilsConfig = {
  devENV?: ENV;
  hasDevEnv?: boolean;
  isDebug?: boolean;
  platform?: Platform;
  setPluginOptions?: ISetPluginOptions;
  env?: ENV;
  /** 项目部署机房环境 */
  machineRoom?: string;
  /** 资源域名子级标识 */
  resChildHost?: string;
  /** 接口请求拦截 */
  requestInterceptor?: Function;
  /** 服务端传入的上下文 */
  context?: any;
  /** 接口 - 签名配置参数（目前用于沙特） */
  clientType?: string;
  /** 常量存储 */
  constant: {
    /** 灰度版本 */
    GRAY_VERSION: string;
    GRAY_VERSION_VUEX: {
      keys: string[];
      dispatchFn: string;
    };
    /** 网络信息 */
    NETWORK_VUEX: {
      keys: string[];
      dispatchFn: string;
    };
    /** APP判断标识(hstong,sahm) */
    APP_FLAG: string[];
    [key: string]: any;
  };
  [key: string]: any;
};
export let utilsConfig: IUtilsConfig & ICommonObject = {
  devENV: ENV.Daily,
  hasDevEnv: false,
  isDebug: false,
  platform: Platform.HST,
  env: ENV.Prod,
  constant: {
    /** 灰度版本 */
    GRAY_VERSION: 'grayVersion',
    GRAY_VERSION_VUEX: {
      keys: ['env', 'grayVersion'],
      dispatchFn: 'env/setGrayVersion',
    },
    NETWORK_VUEX: {
      keys: ['base', 'network'],
      dispatchFn: 'base/setNetwork',
    },
    /** APP判断标识 */
    APP_FLAG: ['hstong', 'sahm'],
  },
};

function getCDN(options: { platform: Platform }) {
  if (!isBrowser()) {
    loggerError('获取CDN失败 - 非浏览器环境', LogModule.Url);
    return;
  }

  const env = getEnv();

  if (options.platform === Platform.SA) {
    return `//${utilsConfig?.resChildHost}${formatterEnv(env, { isCDN: true })}.alsahm.com`;
  }

  if (options.platform === Platform.SG) {
    return `//r${formatterEnv(env, { isCDN: true })}.tickrs.com.sg`;
  }

  if (options.platform === Platform.MY) {
    return env === ENV.Prod
      ? '//r.global.mplusonline.com'
      : `//r${formatterEnv(env, { isCDN: true })}.mplusglobal.com.my`;
  }

  return `//r${formatterEnv(env, { isCDN: true })}.hstong.com`;
}

/**
 * 设置公共Utils配置
 * @param {ENV} devENV 本地环境默认是daily环境，可选成其他环境
 * @param {boolean} hasDevEnv 是否存在dev测试环境
 * @param {boolean} isDebug 设置日志输出
 * @param {Platform} platform 环境
 */
export function setUtilsConfig(options: Partial<IUtilsConfig & ICommonObject>) {
  if (options.constant && !isInvalid(options?.constant?.APP_FLAG)) {
    options.constant.APP_FLAG = utilsConfig.constant.APP_FLAG;
    loggerInfo(['setUtilsConfig方法暂不支持 constant?.APP_FLAG 参数'], LogModule.Config);
  }

  utilsConfig = deepmerge(utilsConfig, options);

  /** 判断是否有传入日志输出 */
  if (isBoolean(options?.isDebug)) {
    setDebug(options.isDebug);
  }

  /** 判断是否为沙特环境 */
  if (options?.platform === Platform.SA) {
    if (isInvalid(utilsConfig?.resChildHost)) {
      if (utilsConfig?.machineRoom === 'oracle') utilsConfig.resChildHost = 'r2';
      else utilsConfig.resChildHost = 'r';
    }

    /** 设置公共插件 */
    const _options =
      (options?.setPluginOptions as ISetPluginOptions) || getCDN({ platform: options.platform });

    /** 灰度标识 - 客户端 */
    if (isInvalid(options?.clientType)) {
      /** 固定传10 */
      utilsConfig.clientType = '10';
    }

    if (_options) {
      // 动态设置插件
      setPluginUrl(_options + '/fe/hs-sa-res/plugins');
    }
  } else {
    utilsConfig.resChildHost = 'r';
  }

  /** 判断是否为新加坡环境 */
  if (options?.platform === Platform.SG) {
    const _options =
      (options?.setPluginOptions as ISetPluginOptions) || getCDN({ platform: options.platform });

    if (_options) {
      setPluginUrl(_options + '/fe/hs-sg-res/plugins');
    }
  }

  /** 判断是否为马来环境 */
  if (options?.platform === Platform.MY) {
    const _options =
      (options?.setPluginOptions as ISetPluginOptions) || getCDN({ platform: options.platform });

    if (_options) {
      setPluginUrl(_options + '/fe/hs-my-res/plugins');
    }
  }
}

export function getUtilsConfig(): IUtilsConfig {
  const SETTING = isBrowser() ? (getBrowser() as any)?.SETTING : {};

  // 设置全局配置参数 - 通过网关传入
  utilsConfig.constant.SETTING = SETTING;

  return utilsConfig;
}

//  -------------- 调试配置 end ---------
